import React, { useEffect, useState } from 'react';
import { Video } from 'lucide-react';
import { fmtDateTime } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-50 text-rose-600 border-rose-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
};

/** Video-call booking — shown to students (own profile) and recruiters
 *  (per student) once the application reaches "LOA Received". */
export default function BookAppointment({ studentId }) {
  const [appointments, setAppointments] = useState([]);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState('');
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/appointments/mine`, { credentials: 'include' });
      if (res.ok) {
        const all = (await res.json()).appointments || [];
        setAppointments(studentId ? all.filter((a) => a.student_id === studentId) : all);
      }
    } catch { /* offline */ }
  };

  useEffect(() => { load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTime(''); setSlots(null);
    if (!date) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/appointments/slots?date=${date}`, { credentials: 'include' });
        if (res.ok) setSlots((await res.json()).slots || []);
      } catch { setSlots([]); }
    })();
  }, [date]);

  const book = async () => {
    setError(''); setSuccess('');
    if (!date || !time) { setError('Please pick a date and time.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date, time, topic: topic.trim(), ...(studentId ? { student_id: studentId } : {}) }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Could not book the appointment.');
        return;
      }
      setDate(''); setTime(''); setTopic(''); setSlots(null);
      setSuccess('Booked! The TGE team has been notified and will confirm shortly.');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-indigo-50/50 border border-indigo-200 rounded-xl p-4" data-testid="book-appointment-section">
      <h4 className="font-semibold text-indigo-900 text-xs uppercase tracking-wider mb-1 flex items-center gap-1.5">
        <Video size={13} /> Book a video call with the TGE team
      </h4>
      <p className="text-xs text-indigo-900/60 mb-3">
        Congratulations on the LOA! Pick a slot and our team will meet you on a video call.
      </p>

      {appointments.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {appointments.map((a) => (
            <div key={a.appointment_id} className="flex items-center gap-2 bg-white border border-indigo-100 rounded-lg px-3 py-1.5 text-xs" data-testid={`appointment-${a.appointment_id}`}>
              <Video size={11} className="text-indigo-500 shrink-0" />
              <span className="font-medium text-tge-navy">{a.date} · {a.time}</span>
              {a.topic && <span className="text-tge-navy/50 truncate">“{a.topic}”</span>}
              <span className={`ml-auto shrink-0 text-[10px] font-semibold uppercase border rounded-full px-2 py-0.5 ${STATUS_STYLE[a.status] || ''}`}>
                {a.status}
              </span>
              {a.meeting_link && a.status === 'confirmed' && (
                <a href={a.meeting_link} target="_blank" rel="noreferrer" className="shrink-0 text-indigo-600 underline font-semibold">Join</a>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
        <input
          type="date"
          min={today}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border border-indigo-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:border-indigo-400"
          data-testid="appointment-date-input"
        />
        <select
          value={time}
          onChange={(e) => setTime(e.target.value)}
          disabled={!date || slots === null}
          className="border border-indigo-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:border-indigo-400 disabled:opacity-50"
          data-testid="appointment-time-select"
        >
          <option value="">{!date ? 'Pick a date first' : slots === null ? 'Loading slots…' : slots.length ? 'Choose a time' : 'No slots (closed / fully booked)'}</option>
          {(slots || []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button
          onClick={book}
          disabled={busy || !date || !time}
          className="text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-lg px-4 py-2 transition"
          data-testid="appointment-book-btn"
        >
          {busy ? 'Booking…' : 'Book video call'}
        </button>
      </div>
      <input
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        placeholder="What would you like to discuss? (optional)"
        className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:border-indigo-400"
        data-testid="appointment-topic-input"
      />
      {error && <div className="text-xs text-rose-600 mt-2" data-testid="appointment-error">{error}</div>}
      {success && <div className="text-xs text-emerald-700 font-semibold mt-2" data-testid="appointment-success">✓ {success}</div>}
      <div className="text-[10px] text-indigo-900/40 mt-2">Office hours: Mon–Fri 8 AM–5 PM · Sat 9 AM–4 PM (Vancouver time)</div>
    </div>
  );
}
